import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import FalconDropzone from "../common/FalconDropzone";
import DownloadFormatDropdown from "../common/DownloadFormatDropdown";
import { Toast } from "../UI/toast/Toast";
import { CurrencyContext } from "../../context/Context";
import { applyExpenseImport, downloadExpenseImportTemplate, previewExpenseImport } from "../../helpers/api/finance";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";

const errorMessage = (error, fallback) =>
  error?.data?.error || Object.values(error?.data?.errors || {}).flat()[0] || fallback;

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const ExpenseImportModal = ({ isOpen, toggle, selectedCondominium, onSuccess }) => {
  const { baseCurrency } = useContext(CurrencyContext);
  const [file, setFile] = useState([]);
  const [result, setResult] = useState(null);
  const [failure, setFailure] = useState(null);
  const [doneMessage, setDoneMessage] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const baseIso = baseCurrency?.iso || "USD";
  const money = (value) => `${baseCurrency?.symbol || "$"} ${formatNumber(value)}`;
  const exampleDate = moment().format("DD/MM/YYYY");
  const exampleRows = [
    ["Ordinario", "Vigilancia", "Servicio de vigilancia del mes", "Servicios básicos", "Seguridad Total", baseIso, "350", ""],
    ["Extraordinario", "Pintura de fachada", "Primera cuota de tres", "Mantenimiento", "Pinturas del Este", baseIso, "1200", ""],
    ["Del condominio", "HIDROVEN", "Agua del mes", "Servicios básicos", "HIDROVEN", "Bs", "122036,49", ""],
    ["Particular", "Reparación de tubería", "Filtración en el baño", "Mantenimiento", "Plomería Rápida", baseIso, "80", "Apto 1A"],
    ["Particular", "Copia de llaves", "Llaves del portón", "Mantenimiento", "Cerrajería Central", "Bs", "1500", "Apto 2B, Apto 3C"],
  ];

  const close = () => {
    if (isChecking || isSaving) return;
    setFile([]);
    setResult(null);
    setFailure(null);
    setDoneMessage(null);
    toggle();
  };

  const buildForm = () => {
    const formData = new FormData();
    formData.append("file", file[0].file);
    return formData;
  };

  const download = async (format) => {
    setIsDownloading(true);
    try {
      const blob = await downloadExpenseImportTemplate(selectedCondominium, format);
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", `gastos.${format}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } catch (error) {
      handleErrorMessage(error);
    } finally {
      setIsDownloading(false);
    }
  };

  const check = async () => {
    if (!file.length) {
      Toast.fire({ type: "error", title: "Por favor selecciona un archivo." });
      return;
    }

    setIsChecking(true);
    setResult(null);
    setFailure(null);
    try {
      const response = await previewExpenseImport(selectedCondominium, buildForm());
      setResult(response?.data || null);
    } catch (error) {
      setFailure(errorMessage(error, "No se pudo revisar el archivo."));
    } finally {
      setIsChecking(false);
    }
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const response = await applyExpenseImport(selectedCondominium, buildForm());
      setResult(response?.data || result);
      setDoneMessage(response?.message || "Los gastos se cargaron.");
      if (onSuccess) onSuccess();
    } catch (error) {
      if (error?.data?.data) {
        setResult(error.data.data);
      }
      setFailure(errorMessage(error, "No se pudieron cargar los gastos."));
    } finally {
      setIsSaving(false);
    }
  };

  const summary = result?.summary;
  const rows = result?.rows || [];
  const errors = result?.errors || [];
  const warnings = result?.warnings || [];
  const rowLabel = (item) => `Fila ${item.row}${item.label ? ` · ${item.label}` : ""}`;

  return (
    <Modal isOpen={isOpen} toggle={close} size="xl" centered>
      <ModalHeader toggle={close}>Importar gastos</ModalHeader>
      <ModalBody className="fs--1">
        {doneMessage ? (
          <Alert color="success" className="mb-3">
            {doneMessage}
          </Alert>
        ) : (
          <>
            <p className="mb-2">
              Descarga la plantilla, llena una fila por gasto y súbela. Antes de guardar se revisa todo y ves lo que se
              va a cargar. Si una fila tiene un problema, no se carga ningún gasto.
            </p>
            <ul className="pl-3 mb-2">
              <li>
                <strong>Tipo:</strong> Ordinario, Extraordinario, Del condominio o Particular. Los particulares llevan en
                Inmuebles a quién se le cobran (uno, varios separados por coma, o Todos) y el monto se reparte en partes
                iguales.
              </li>
              <li>
                <strong>{`Moneda: ${baseIso} o Bs.`}</strong>
                {` Los bolívares se pasan a ${baseIso} con la tasa de la fecha del gasto, igual que en el formulario.`}
              </li>
              <li>
                El Área contable y el Proveedor son obligatorios. Si alguno no está registrado, se crea al confirmar (el
                proveedor, solo con su nombre); la revisión te avisa antes.
              </li>
              <li>
                Puedes descargar la plantilla y subirla en Excel, CSV o texto. En Excel, el Tipo, la Moneda, el Área
                contable y el Proveedor se eligen de una lista desplegable, y trae una hoja con las áreas, los proveedores
                y los inmuebles, y las instrucciones. En CSV y texto vienen solo los encabezados y se escriben a mano: da
                igual si van en mayúsculas o sin tildes.
              </li>
            </ul>
            <p className="font-weight-semi-bold mb-1">Ejemplo, una fila de cada tipo:</p>
            <Table size="sm" bordered responsive className="fs--2 mb-1 bg-white">
              <thead>
                <tr>
                  {["Tipo", "Nombre", "Descripción", "Área contable", "Proveedor", "Fecha", "Moneda", "Monto", "Inmuebles"].map(
                    (header) => (
                      <th key={header} className="text-nowrap">
                        {header}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {exampleRows.map(([type, name, description, area, provider, currency, amount, units]) => (
                  <tr key={name}>
                    <td className="text-nowrap">{type}</td>
                    <td className="text-nowrap">{name}</td>
                    <td>{description}</td>
                    <td className="text-nowrap">{area}</td>
                    <td className="text-nowrap">{provider}</td>
                    <td className="text-nowrap">{exampleDate}</td>
                    <td>{currency}</td>
                    <td className="text-right text-nowrap">{amount}</td>
                    <td className="text-nowrap">{units}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <p className="text-700 mb-3">
              {`Inmuebles va vacío en los gastos que no son particulares. Los montos en Bs se pasan a ${baseIso} con la tasa de la fecha de cada gasto. La copia de llaves se cobra a los dos apartamentos, en partes iguales.`}
            </p>
            <DownloadFormatDropdown
              label={isDownloading ? "Descargando..." : "Descargar plantilla de gastos"}
              onSelect={download}
              disabled={isDownloading}
              color="falcon-default"
              size="sm"
              className="mb-3"
            />

            <FalconDropzone
              files={file}
              onChange={(files) => {
                setFile(files);
                setResult(null);
                setFailure(null);
              }}
              multiple={false}
              preview
              accept=".xlsx, .xls, .csv, .txt"
              placeholder={
                <>
                  <div className="fs-2 mb-2">
                    <FontAwesomeIcon icon={faFileExcel} />
                  </div>
                  <p className="fs--1 mb-0">Arrastra tu archivo aquí o haz clic</p>
                </>
              }
            />
          </>
        )}

        {failure && !errors.length && (
          <Alert color="danger" className="mt-3 mb-0">
            {failure}
          </Alert>
        )}

        {errors.length > 0 && (
          <Alert color="danger" className="mt-3 mb-0">
            <p className="font-weight-semi-bold mb-2">
              {`Hay ${errors.length} fila(s) con problemas, así que no se puede cargar ningún gasto. Corrígelas y vuelve a subir el archivo.`}
            </p>
            <div style={{ maxHeight: 220, overflowY: "auto" }}>
              {errors.map((error) => (
                <div key={`error-${error.row}`} className="mb-2">
                  <div className="font-weight-semi-bold">{rowLabel(error)}</div>
                  <ul className="pl-3 mb-0">
                    {error.messages.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Alert>
        )}

        {summary && rows.length > 0 && (
          <div className="mt-3">
            <p className="font-weight-semi-bold mb-1">
              {doneMessage
                ? `Se cargaron ${summary.count} gasto(s) por ${money(summary.total)}:`
                : errors.length
                ? `Filas sin problemas (${summary.count}):`
                : `Se van a cargar ${summary.count} gasto(s) por ${money(summary.total)}:`}
            </p>
            {!errors.length && (
              <ul className="pl-3 mb-2">
                {summary.by_type.map((item) => (
                  <li key={item.type}>{`${item.type}: ${item.count} por ${money(item.total)}`}</li>
                ))}
                {summary.new_areas?.length > 0 && (
                  <li>
                    {`${doneMessage ? "Se crearon" : "Se crean"} las áreas contables: ${summary.new_areas.join(", ")}.`}
                  </li>
                )}
                {summary.new_providers?.length > 0 && (
                  <li>
                    {`${doneMessage ? "Se crearon" : "Se crean"} los proveedores: ${summary.new_providers.join(", ")}.`}
                  </li>
                )}
              </ul>
            )}
            <div style={{ maxHeight: 260, overflowY: "auto" }}>
              <Table size="sm" bordered className="mb-0 bg-white">
                <thead>
                  <tr>
                    <th>Fila</th>
                    <th>Tipo</th>
                    <th>Nombre</th>
                    <th>Fecha</th>
                    <th className="text-right">Monto del archivo</th>
                    <th className="text-right">{`Monto (${baseIso})`}</th>
                    <th>Inmuebles</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.row}>
                      <td>{row.row}</td>
                      <td>{row.type}</td>
                      <td>{row.name}</td>
                      <td>{moment(row.date).format("DD/MM/YYYY")}</td>
                      <td className="text-right text-nowrap">{`${row.currency} ${formatNumber(row.original_amount)}`}</td>
                      <td className="text-right text-nowrap">{money(row.amount)}</td>
                      <td>{row.units || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}

        {warnings.length > 0 && !doneMessage && (
          <Alert color="warning" className="mt-3 mb-0">
            <p className="font-weight-semi-bold mb-2">Revisa esto antes de confirmar:</p>
            <div style={{ maxHeight: 180, overflowY: "auto" }}>
              {warnings.map((warning, index) => (
                <div key={`warning-${index}`} className="mb-1">
                  <span className="font-weight-semi-bold">{`${rowLabel(warning)}: `}</span>
                  {warning.messages.join(" ")}
                </div>
              ))}
            </div>
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="falcon-default" onClick={close} disabled={isChecking || isSaving}>
          {doneMessage ? "Cerrar" : "Volver"}
        </Button>
        {!doneMessage &&
          (result?.can_apply ? (
            <Button color="primary" onClick={save} disabled={isSaving}>
              {isSaving ? <Spinner size="sm" /> : "Confirmar carga"}
            </Button>
          ) : (
            <Button color="primary" onClick={check} disabled={isChecking || !file.length}>
              {isChecking ? <Spinner size="sm" /> : "Revisar archivo"}
            </Button>
          ))}
      </ModalFooter>
    </Modal>
  );
};

ExpenseImportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  selectedCondominium: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSuccess: PropTypes.func,
};

export default ExpenseImportModal;
